import React, { useState, useEffect, useMemo, useRef } from "react";
import Latex from "react-latex";
import "katex/dist/katex.min.css";
import he from "he";
import Filter from "../../Assets/Images/CommanImages/filter.png";
import columnaddition from "../../Assets/Images/CommanImages/rectangleplus.png";
import updown from "../../Assets/Images/CommanImages/updown.png";
import searchicon from "../../Assets/Images/CommanImages/searchyellow.png";
import leftArrow from "../../Assets/Images/CommanImages/Larrowbar.png";
import rightArrow from "../../Assets/Images/CommanImages/Rarrowbar.png";
import SelectedValuesDropdown from "../../Components/Shared/SelectedValuesDropdown/SelectDropdown";
import FacultyReview from "../Parser/FacultyReview/FacultyReview";
import "./quicldppscheduling.css";
import QuickQuesPaperModal from "./QuickQuesPaperModal";

// columns that can't be hidden from Column Selection
const LOCKED_COLUMNS = ["Q No", "Question Text"];

// columns that don't make sense as filter dropdowns
const NON_FILTER_COLUMNS = ["Question Text"];

// Remove HTML tags (<p>, <br>, etc.) so they don't show as literal text
const stripHtml = (s) =>
  String(s ?? "")
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<[^>]*>/g, " ");

// Same pattern as renderQuestionText in the dashboard
const renderTextSmall = (raw) => {
  const decoded = he.decode(stripHtml(raw));
  return decoded.split(/(imgsrc=[^ ]+)/g).map((seg, i) =>
    seg.startsWith("imgsrc=") ? (
      <img
        key={i}
        src={seg.replace("imgsrc=", "")}
        alt={`Question Image ${i}`}
        style={{ width: "30px", height: "auto", margin: "5px" }}
      />
    ) : (
      <span key={i} className="truncated-text-2">
        <Latex>{seg}</Latex>
      </span>
    )
  );
};

const QuickDPPSchedulingTable = ({
  parseQuesSetId,
  headers = [],
  data = [],
  loading = false,
  error = "",
  onRefresh,
  setMessageBox,
  setIsMessageBoxVisible,
  setActiveImg,
  dataClient,
}) => {
  const rows = data;

  const [searchQuery, setSearchQuery] = useState("");
  const [visibleColumns, setVisibleColumns] = useState([]);
  const [isColumnDivVisible, setIsColumnDivVisible] = useState(false);
  const [isFilterVisible, setIsFilterVisible] = useState(false);
  const [filters, setFilters] = useState({});
  const [appliedFilters, setAppliedFilters] = useState({});
  const [isFiltersApplied, setIsFiltersApplied] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedQuestionId, setSelectedQuestionId] = useState(null);

  const columnDropdownRef = useRef(null);

  // column resize (Question Text)
  const [columnWidth, setColumnWidth] = useState(400);
  const columnRef = useRef(null);
  const isResizing = useRef(false);

  // show all columns once headers arrive (keeps user's selection on refresh)
  useEffect(() => {
    if (headers.length > 0) {
      setVisibleColumns((prev) => (prev.length ? prev : headers));
    }
  }, [headers]);

  // reset to first page whenever fresh data comes in
  useEffect(() => {
    setCurrentPage(1);
  }, [data]);

  // close column dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        columnDropdownRef.current &&
        !columnDropdownRef.current.contains(event.target)
      ) {
        setIsColumnDivVisible(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ── search / filter / sort ───────────────────────────────────────────────
  const filteredRows = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return rows.filter((r) => {
      const matchesSearch = Object.values(r.cells).some(
        (c) => c !== null && c !== undefined && String(c).toLowerCase().includes(q)
      );
      const matchesFilters = Object.entries(appliedFilters).every(
        ([key, value]) => (value ? String(r.cells[key]) === String(value) : true)
      );
      return matchesSearch && matchesFilters;
    });
  }, [rows, searchQuery, appliedFilters]);

  const sortedRows = useMemo(() => {
    const sortable = [...filteredRows];
    if (sortConfig.key) {
      sortable.sort((a, b) => {
        const av = a.cells[sortConfig.key] ?? "";
        const bv = b.cells[sortConfig.key] ?? "";
        const res = String(av).localeCompare(String(bv), undefined, {
          numeric: true,
          sensitivity: "base",
        });
        return sortConfig.direction === "asc" ? res : -res;
      });
    }
    return sortable;
  }, [filteredRows, sortConfig]);

  // all question ids from the table, comma separated
  const allQuestionIds = useMemo(
    () => rows.map((r) => r.id).join(","),
    [rows]
  );

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / pageSize));
  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return sortedRows.slice(start, start + pageSize);
  }, [sortedRows, currentPage, pageSize]);

  // ── handlers ─────────────────────────────────────────────────────────────
  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const handleSort = (column) => {
    let direction = "asc";
    if (sortConfig.key === column && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key: column, direction });
  };

  const handleColumnSelection = (column) => {
    if (LOCKED_COLUMNS.includes(column)) return;
    setVisibleColumns((prev) =>
      prev.includes(column) ? prev.filter((c) => c !== column) : [...prev, column]
    );
  };

  const handleApplyFilter = () => {
    if (Object.keys(filters).length > 0) {
      setAppliedFilters({ ...filters });
      setCurrentPage(1);
      setIsFiltersApplied(true);
      setIsFilterVisible(false);
    }
  };

  const handleResetFilter = () => {
    setFilters({});
    setAppliedFilters({});
    setIsFiltersApplied(false);
    setCurrentPage(1);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => setCurrentPage(page);

  const closeReviewModal = () => {
    setSelectedQuestionId(null);
    if (onRefresh) onRefresh(); // parent re-calls the API
  };

  // column resize handlers
  const handleMouseMove = (event) => {
    if (isResizing.current && columnRef.current) {
      const left = columnRef.current.getBoundingClientRect().left;
      setColumnWidth(Math.max(100, event.clientX - left));
    }
  };

  const handleMouseUp = () => {
    isResizing.current = false;
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
  };

  const handleMouseDown = () => {
    isResizing.current = true;
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const orderedVisible = headers.filter((h) => visibleColumns.includes(h));

  // ── pagination (same layout as TableComponent) ───────────────────────────
  const pageButton = (i) => (
    <button
      key={i}
      onClick={() => handlePageChange(i)}
      style={{ borderRadius: "5px" }}
      className={i === currentPage ? "active" : ""}
    >
      {i}
    </button>
  );

  const dots = (key) => (
    <span style={{ borderRadius: "5px" }} key={key}>
      ...
    </span>
  );

  const renderPagination = () => {
    const pages = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) pages.push(pageButton(i));
    } else if (currentPage <= 3) {
      for (let i = 1; i <= 3; i++) pages.push(pageButton(i));
      pages.push(dots("dots1"));
      pages.push(pageButton(totalPages));
    } else if (currentPage < totalPages - 2) {
      pages.push(pageButton(1));
      pages.push(dots("dots1"));
      for (let i = currentPage - 1; i <= currentPage + 1; i++)
        pages.push(pageButton(i));
      pages.push(dots("dots2"));
      pages.push(pageButton(totalPages));
    } else {
      pages.push(pageButton(1));
      pages.push(dots("dots1"));
      for (let i = totalPages - 2; i <= totalPages; i++) pages.push(pageButton(i));
    }
    return pages;
  };

  if (!parseQuesSetId) return null;

  return (
    <div style={{ width: "100%", marginTop: "10px" }}>
      <div
        style={{
          fontSize: "16px",
          color: "#2F2E40",
          fontWeight: "bold",
          marginBottom: "8px",
        }}
      >
        QUESTIONS LIST
      </div>

      {/* search + records info */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginTop: "1.5%",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              border: "1px solid #6A6A6A",
              overflow: "hidden",
            }}
          >
            <input
              type="text"
              placeholder="Filter Questions..."
              name="search"
              className="search-input-ques-dash"
              value={searchQuery}
              onChange={handleSearch}
            />
            <div
              style={{
                width: "1px",
                height: "24px",
                backgroundColor: "#2F2E40",
                margin: "0 4px",
              }}
            ></div>
            <img
              src={searchicon}
              alt="search"
              className="search-icon-ques-dash"
            />
          </div>
          <div
            style={{ textAlign: "end", marginTop: "20px", color: "#424242" }}
          >
            Displaying{" "}
            {sortedRows.length === 0 ? 0 : (currentPage - 1) * pageSize + 1} -{" "}
            {Math.min(currentPage * pageSize, sortedRows.length)} of{" "}
            {sortedRows.length} records |
            {["10", "25", "50", "100", "500"].map((size) => (
              <span
                key={size}
                onClick={() => handlePageSizeChange(Number(size))}
                style={{
                  cursor: "pointer",
                  margin: "0 5px",
                  textDecoration:
                    size === pageSize.toString() ? "none" : "underline",
                  color: size === pageSize.toString() ? "black" : "#337AB7",
                }}
              >
                {size}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* toolbar: Filter + Column Selection */}
      <div
        style={{
          display: "flex",
          backgroundColor: "#D6D6D6",
          marginTop: "9px",
          borderTop: "1px solid grey",
          borderBottom: "1px solid grey",
        }}
      >
        <div
          style={{
            backgroundColor: "#FFBD20",
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
            paddingLeft: "8px",
            width: "90px",
          }}
          onClick={() => setIsFilterVisible(!isFilterVisible)}
        >
          <img
            src={Filter}
            style={{ height: "24px", width: "24px" }}
            alt="filter"
          />
          <span style={{ marginLeft: "10px", color: "#2F2E40" }}>Filter</span>
        </div>

        <div
          className="col-tc"
          style={{
            marginLeft: "auto",
            textAlign: "end",
            marginRight: "8px",
            display: "flex",
            alignItems: "center",
            position: "relative",
            cursor: "pointer",
            height: "40px",
          }}
          onClick={() => setIsColumnDivVisible(!isColumnDivVisible)}
        >
          <img
            src={columnaddition}
            style={{ height: "34px", width: "32px" }}
            alt="column addition"
          />
          <span
            style={{
              marginLeft: "4px",
              color: "#2F2E40",
              marginRight: "14px",
              fontSize: "14px",
            }}
          >
            Column Selection
          </span>
          {isColumnDivVisible && (
            <div ref={columnDropdownRef}>
              <div className="arrow-up-tc"></div>
              <div className="popupColumn-tc">
                {headers.map((column, index) => (
                  <div
                    key={index}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleColumnSelection(column);
                    }}
                  >
                    <input
                      type="checkbox"
                      id={`dpp-column-${index}`}
                      value={column}
                      className="square-checkbox-tc"
                      checked={visibleColumns.includes(column)}
                      disabled={LOCKED_COLUMNS.includes(column)}
                      readOnly
                    />
                    <label htmlFor={`dpp-column-${index}`}>{column}</label>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* table */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "20px" }}>
          Loading questions...
        </div>
      ) : error ? (
        <div style={{ color: "red", padding: "10px" }}>{error}</div>
      ) : sortedRows.length === 0 ? (
        <div style={{ padding: "10px" }}>No data available</div>
      ) : (
        <>
          <div className="table-wrapper-cols-selections">
            <table className="table-tc" style={{ width: "100%" }}>
              <thead>
                <tr>
                  {orderedVisible.map((heading) => (
                    <td
                      key={heading}
                      ref={heading === "Question Text" ? columnRef : null}
                      className="Heading-tc resizable-header"
                      style={{
                        textAlign: "right",
                        cursor: "pointer",
                        position: "relative",
                        width:
                          heading === "Question Text"
                            ? `${columnWidth}px`
                            : "auto",
                      }}
                    >
                      <span
                        style={{
                          display: "flex",
                          alignItems: "left",
                          justifyContent: "left",
                        }}
                      >
                        {heading}
                        {heading === "Question Text" && (
                          <div
                            className="resize-handle"
                            onMouseDown={handleMouseDown}
                          ></div>
                        )}
                        <img
                          onClick={() => handleSort(heading)}
                          src={updown}
                          style={{
                            height: "20px",
                            width: "20px",
                            marginLeft: "5px",
                          }}
                          alt="sort"
                        />
                      </span>
                    </td>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginatedRows.map((row, rowIndex) => (
                  <tr
                    key={row.id}
                    className={rowIndex % 2 === 1 ? "even-row-tc" : ""}
                    style={ Number(row.status) === 5 ? { backgroundColor: "#FDECEC" } : undefined }
                  >
                    {orderedVisible.map((column) => (
                      <td
                        key={column}
                        style={{
                          textAlign: "left",
                          verticalAlign: "middle",
                          color: Number(row.status) === 5 ? "#D32F2F" : "#6A6A6A",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth:
                            column === "Question Text"
                              ? `${columnWidth}px`
                              : undefined,
                        }}
                      >
                        {column === "Question Text" ? (
                          <div
                            style={{
                              color: Number(row.status) === 5 ? "#D32F2F" : "#337ab7",
                              cursor: "pointer",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                            onClick={() => setSelectedQuestionId(row.id)}
                          >
                            {renderTextSmall(row.cells[column])}
                          </div>
                        ) : (
                          <span className="truncated-text-2">
                            {row.cells[column]}
                          </span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="pagination-tc">
            <button
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
              className="extreme-left-tc-pagination"
            >
              <img src={leftArrow} alt="" />
            </button>
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="lefttc-pagination"
            >
              {"<"}
            </button>
            {renderPagination()}
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="righttc-pagination"
            >
              {">"}
            </button>
            <button
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages}
              className="extreme-right-tc-pagination"
            >
              <img src={rightArrow} alt="" />
            </button>
          </div>
        </>
      )}

      {/* filter sidebar */}
      {isFilterVisible && (
        <div className="filter-sidebar-tc">
          <div className="filter-content-tc" style={{ position: "relative" }}>
            <button
              className="repman-cross-btn-HOD"
              onClick={() => setIsFilterVisible(false)}
            >
              <p>X</p>
            </button>
            <h3 className="AdvanceF-heading-tc"> Filter</h3>
            <div className="filter-apply-reset-ques-rev-dash">
              <button
                className="create-test-section-btn"
                onClick={handleApplyFilter}
              >
                {" "}
                APPLY{" "}
              </button>
              {isFiltersApplied && (
                <button
                  className="create-test-section-btn"
                  onClick={handleResetFilter}
                >
                  {" "}
                  RESET{" "}
                </button>
              )}
            </div>
            <div className="filter-options-container">
              {headers.filter((h) => !NON_FILTER_COLUMNS.includes(h)).map(
                (header) => {
                  const uniqueOptions = [
                    ...new Set(
                      rows
                        .map((r) => r.cells[header])
                        .filter(
                          (v) =>
                            v !== null &&
                            v !== undefined &&
                            String(v).trim() !== ""
                        )
                    ),
                  ].sort((a, b) =>
                    String(a).localeCompare(String(b), undefined, {
                      numeric: true,
                    })
                  );
                  return (
                    <SelectedValuesDropdown
                      key={header}
                      label={header}
                      width="300px"
                      marginLeft="15px"
                      marginTop="18px"
                      setSelected={(selectedOption) =>
                        setFilters((prev) => ({
                          ...prev,
                          [header]: selectedOption,
                        }))
                      }
                      options={uniqueOptions.map((value) => ({
                        label: String(value),
                        id: value,
                      }))}
                      selected={filters[header] || ""}
                      Height="auto"
                      OverFlow="auto"
                      minheight="200px"
                    />
                  );
                }
              )}
            </div>
          </div>
        </div>
      )}

      {/* faculty review modal */}
      {selectedQuestionId !== null && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(0,0,0,0.5)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div style={{ position: "relative", width: "84vw", height: "80vh" }}>
            <div
              onClick={closeReviewModal}
              aria-label="Close"
              title="Close"
              style={{
                position: "absolute",
                top: "-10px",
                right: "-38px",
                color: "#fff",
                fontSize: "30px",
                cursor: "pointer",
                userSelect: "none",
                zIndex: 1001,
              }}
            >
              ×
            </div>

            <div
              style={{
                background: "#fff",
                width: "100%",
                height: "100%",
                overflow: "auto",
                borderRadius: "14px",
                boxShadow: "0 4px 24px rgba(0,0,0,0.25)",
              }}
            >
              <QuickQuesPaperModal
                isModal={true}
                onClose={closeReviewModal}
                modalSetId={parseQuesSetId}
                modalQuestionNumbers={allQuestionIds}
                modalInitialQuestionId={selectedQuestionId}
                setMessageBox={setMessageBox}
                setIsMessageBoxVisible={setIsMessageBoxVisible}
                setActiveImg={setActiveImg}
                dataClient={dataClient}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuickDPPSchedulingTable;

































// import React, { useState, useEffect, useMemo, useRef } from "react";
// import Latex from "react-latex";
// import "katex/dist/katex.min.css";
// import he from "he";
// import { FULL_API_BASE_URL } from "../../Config/Config";
// import Filter from "../../Assets/Images/CommanImages/filter.png";
// import columnaddition from "../../Assets/Images/CommanImages/rectangleplus.png";
// import updown from "../../Assets/Images/CommanImages/updown.png";
// import searchicon from "../../Assets/Images/CommanImages/searchyellow.png";
// import leftArrow from "../../Assets/Images/CommanImages/Larrowbar.png";
// import rightArrow from "../../Assets/Images/CommanImages/Rarrowbar.png";
// import SelectedValuesDropdown from "../../Components/Shared/SelectedValuesDropdown/SelectDropdown";
// import FacultyReview from "../Parser/FacultyReview/FacultyReview";
// import "./quicldppscheduling.css";
// import QuickQuesPaperModal from "./QuickQuesPaperModal";


// const HEADERS = [
//   "Q No",
//   "Question Text",
//   "Question Type",
//   "Chapter",
//   "Topic",
//   "Difficulty Level",
//   "Status",
//   "Course",
//   "Subject",
// ];

// // columns that can't be hidden from Column Selection
// const LOCKED_COLUMNS = ["Q No", "Question Text"];

// // columns that don't make sense as filter dropdowns
// const NON_FILTER_COLUMNS = ["Question Text"];

// const getTag = (q, name) =>
//   q?.tags?.find((t) => t.tag_name === name)?.tag_value || "";

// // Remove HTML tags (<p>, <br>, etc.) so they don't show as literal text
// const stripHtml = (s) =>
//   String(s ?? "")
//     .replace(/<br\s*\/?>/gi, " ")
//     .replace(/<[^>]*>/g, " ");

// const buildRow = (q, index) => ({
//   id: q.question_id ?? q.id ?? `row-${index}`,
//   status: q.status,
//   cells: {
//     "Q No": q.ques_no ?? index + 1,
//     "Question Text": q.question_text || "",
//     "Question Type": q.ques_type_text || q.ques_type || "",
//     Chapter: getTag(q, "Chapter"),
//     Topic: getTag(q, "Topic"),
//     "Difficulty Level": getTag(q, "Difficulty Level"),
//     Status: q.status_txt || "",
//     Course: q.course_name || "",
//     Subject: q.subject_name || "",
//   },
// });

// // Same pattern as renderQuestionText in the dashboard
// const renderTextSmall = (raw) => {
//   const decoded = he.decode(stripHtml(raw));
//   return decoded.split(/(imgsrc=[^ ]+)/g).map((seg, i) =>
//     seg.startsWith("imgsrc=") ? (
//       <img
//         key={i}
//         src={seg.replace("imgsrc=", "")}
//         alt={`Question Image ${i}`}
//         style={{ width: "30px", height: "auto", margin: "5px" }}
//       />
//     ) : (
//       <span key={i} className="truncated-text-2">
//         <Latex>{seg}</Latex>
//       </span>
//     )
//   );
// };

// const QuickDPPSchedulingTable = ({
//   parseQuesSetId,
//   clientId,
//   clientBranchId,
//   setMessageBox,
//   setIsMessageBoxVisible,
//   setActiveImg,
//   dataClient,
// }) => {
//   const accessToken = localStorage.getItem("other");

//   const [rows, setRows] = useState([]);
//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState("");

//   const [searchQuery, setSearchQuery] = useState("");
//   const [visibleColumns, setVisibleColumns] = useState(HEADERS);
//   const [isColumnDivVisible, setIsColumnDivVisible] = useState(false);
//   const [isFilterVisible, setIsFilterVisible] = useState(false);
//   const [filters, setFilters] = useState({});
//   const [appliedFilters, setAppliedFilters] = useState({});
//   const [isFiltersApplied, setIsFiltersApplied] = useState(false);
//   const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
//   const [currentPage, setCurrentPage] = useState(1);
//   const [pageSize, setPageSize] = useState(10);
//   const [selectedQuestionId, setSelectedQuestionId] = useState(null);
//   const [refreshKey, setRefreshKey] = useState(0);

//   const columnDropdownRef = useRef(null);

//   // column resize (Question Text)
//   const [columnWidth, setColumnWidth] = useState(400);
//   const columnRef = useRef(null);
//   const isResizing = useRef(false);

//   // ── fetch ────────────────────────────────────────────────────────────────
//   useEffect(() => {
//     if (!parseQuesSetId || !clientId || !clientBranchId) {
//       setRows([]);
//       return;
//     }
//     const fetchParsedQuestions = async () => {
//       setLoading(true);
//       setError("");
//       try {
//         const response = await fetch(
//           `${FULL_API_BASE_URL}/get-parsed-questions/?client_id=${clientId}&client_branch_id=${clientBranchId}&parse_ques_set_id=${parseQuesSetId}`,
//           {
//             method: "GET",
//             headers: {
//               accept: "*/*",
//               Authorization: `Bearer ${accessToken}`,
//             },
//           }
//         );
//         if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
//         const result = await response.json();
//         const list = Array.isArray(result)
//           ? result
//           : Array.isArray(result?.data)
//           ? result.data
//           : [];
//         setRows(list.map((q, i) => buildRow(q, i)));
//         setCurrentPage(1);
//       } catch (err) {
//         console.error("Error fetching parsed questions:", err);
//         setError(err.message || "Failed to load questions");
//         setRows([]);
//       } finally {
//         setLoading(false);
//       }
//     };
//     fetchParsedQuestions();
//   }, [parseQuesSetId, clientId, clientBranchId, refreshKey]);

//   // close column dropdown on outside click
//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       if (
//         columnDropdownRef.current &&
//         !columnDropdownRef.current.contains(event.target)
//       ) {
//         setIsColumnDivVisible(false);
//       }
//     };
//     document.addEventListener("mousedown", handleClickOutside);
//     return () => document.removeEventListener("mousedown", handleClickOutside);
//   }, []);

//   // ── search / filter / sort ───────────────────────────────────────────────
//   const filteredRows = useMemo(() => {
//     const q = searchQuery.toLowerCase();
//     return rows.filter((r) => {
//       const matchesSearch = Object.values(r.cells).some(
//         (c) => c !== null && c !== undefined && String(c).toLowerCase().includes(q)
//       );
//       const matchesFilters = Object.entries(appliedFilters).every(
//         ([key, value]) => (value ? String(r.cells[key]) === String(value) : true)
//       );
//       return matchesSearch && matchesFilters;
//     });
//   }, [rows, searchQuery, appliedFilters]);

//   const sortedRows = useMemo(() => {
//     const data = [...filteredRows];
//     if (sortConfig.key) {
//       data.sort((a, b) => {
//         const av = a.cells[sortConfig.key] ?? "";
//         const bv = b.cells[sortConfig.key] ?? "";
//         const res = String(av).localeCompare(String(bv), undefined, {
//           numeric: true,
//           sensitivity: "base",
//         });
//         return sortConfig.direction === "asc" ? res : -res;
//       });
//     }
//     return data;
//   }, [filteredRows, sortConfig]);


//     // all question ids from the table, comma separated
//   const allQuestionIds = useMemo(
//     () => rows.map((r) => r.id).join(","),
//     [rows]
//   );

//   const totalPages = Math.max(1, Math.ceil(sortedRows.length / pageSize));
//   const paginatedRows = useMemo(() => {
//     const start = (currentPage - 1) * pageSize;
//     return sortedRows.slice(start, start + pageSize);
//   }, [sortedRows, currentPage, pageSize]);

//   // ── handlers ─────────────────────────────────────────────────────────────
//   const handleSearch = (e) => {
//     setSearchQuery(e.target.value);
//     setCurrentPage(1);
//   };

//   const handleSort = (column) => {
//     let direction = "asc";
//     if (sortConfig.key === column && sortConfig.direction === "asc") {
//       direction = "desc";
//     }
//     setSortConfig({ key: column, direction });
//   };

//   const handleColumnSelection = (column) => {
//     if (LOCKED_COLUMNS.includes(column)) return;
//     setVisibleColumns((prev) =>
//       prev.includes(column) ? prev.filter((c) => c !== column) : [...prev, column]
//     );
//   };

//   const handleApplyFilter = () => {
//     if (Object.keys(filters).length > 0) {
//       setAppliedFilters({ ...filters });
//       setCurrentPage(1);
//       setIsFiltersApplied(true);
//       setIsFilterVisible(false);
//     }
//   };

//   const handleResetFilter = () => {
//     setFilters({});
//     setAppliedFilters({});
//     setIsFiltersApplied(false);
//     setCurrentPage(1);
//   };

//   const handlePageSizeChange = (size) => {
//     setPageSize(size);
//     setCurrentPage(1);
//   };

//   const handlePageChange = (page) => setCurrentPage(page);

//   const closeReviewModal = () => {
//     setSelectedQuestionId(null);
//     setRefreshKey((k) => k + 1);
//   };

//   // column resize handlers
//   const handleMouseMove = (event) => {
//     if (isResizing.current && columnRef.current) {
//       const left = columnRef.current.getBoundingClientRect().left;
//       setColumnWidth(Math.max(100, event.clientX - left));
//     }
//   };

//   const handleMouseUp = () => {
//     isResizing.current = false;
//     document.removeEventListener("mousemove", handleMouseMove);
//     document.removeEventListener("mouseup", handleMouseUp);
//   };

//   const handleMouseDown = () => {
//     isResizing.current = true;
//     document.addEventListener("mousemove", handleMouseMove);
//     document.addEventListener("mouseup", handleMouseUp);
//   };

//   const orderedVisible = HEADERS.filter((h) => visibleColumns.includes(h));

//   // ── pagination (same layout as TableComponent) ───────────────────────────
//   const pageButton = (i) => (
//     <button
//       key={i}
//       onClick={() => handlePageChange(i)}
//       style={{ borderRadius: "5px" }}
//       className={i === currentPage ? "active" : ""}
//     >
//       {i}
//     </button>
//   );

//   const dots = (key) => (
//     <span style={{ borderRadius: "5px" }} key={key}>
//       ...
//     </span>
//   );

//   const renderPagination = () => {
//     const pages = [];
//     if (totalPages <= 5) {
//       for (let i = 1; i <= totalPages; i++) pages.push(pageButton(i));
//     } else if (currentPage <= 3) {
//       for (let i = 1; i <= 3; i++) pages.push(pageButton(i));
//       pages.push(dots("dots1"));
//       pages.push(pageButton(totalPages));
//     } else if (currentPage < totalPages - 2) {
//       pages.push(pageButton(1));
//       pages.push(dots("dots1"));
//       for (let i = currentPage - 1; i <= currentPage + 1; i++)
//         pages.push(pageButton(i));
//       pages.push(dots("dots2"));
//       pages.push(pageButton(totalPages));
//     } else {
//       pages.push(pageButton(1));
//       pages.push(dots("dots1"));
//       for (let i = totalPages - 2; i <= totalPages; i++) pages.push(pageButton(i));
//     }
//     return pages;
//   };

//   if (!parseQuesSetId) return null;

//   return (
//     <div style={{ width: "100%", marginTop: "10px" }}>
//       <div
//         style={{
//           fontSize: "16px",
//           color: "#2F2E40",
//           fontWeight: "bold",
//           marginBottom: "8px",
//         }}
//       >
//         QUESTIONS LIST
//       </div>

//       {/* search + records info */}
//       <div
//         style={{
//           display: "flex",
//           alignItems: "center",
//           justifyContent: "space-between",
//           marginTop: "1.5%",
//         }}
//       >
//         <div
//           style={{
//             display: "flex",
//             alignItems: "center",
//             justifyContent: "space-between",
//             width: "100%",
//           }}
//         >
//           <div
//             style={{
//               display: "flex",
//               alignItems: "center",
//               border: "1px solid #6A6A6A",
//               overflow: "hidden",
//             }}
//           >
//             <input
//               type="text"
//               placeholder="Filter Questions..."
//               name="search"
//               className="search-input-ques-dash"
//               value={searchQuery}
//               onChange={handleSearch}
//             />
//             <div
//               style={{
//                 width: "1px",
//                 height: "24px",
//                 backgroundColor: "#2F2E40",
//                 margin: "0 4px",
//               }}
//             ></div>
//             <img
//               src={searchicon}
//               alt="search"
//               className="search-icon-ques-dash"
//             />
//           </div>
//           <div
//             style={{ textAlign: "end", marginTop: "20px", color: "#424242" }}
//           >
//             Displaying{" "}
//             {sortedRows.length === 0 ? 0 : (currentPage - 1) * pageSize + 1} -{" "}
//             {Math.min(currentPage * pageSize, sortedRows.length)} of{" "}
//             {sortedRows.length} records |
//             {["10", "25", "50", "100", "500"].map((size) => (
//               <span
//                 key={size}
//                 onClick={() => handlePageSizeChange(Number(size))}
//                 style={{
//                   cursor: "pointer",
//                   margin: "0 5px",
//                   textDecoration:
//                     size === pageSize.toString() ? "none" : "underline",
//                   color: size === pageSize.toString() ? "black" : "#337AB7",
//                 }}
//               >
//                 {size}
//               </span>
//             ))}
//           </div>
//         </div>
//       </div>

//       {/* toolbar: Filter + Column Selection */}
//       <div
//         style={{
//           display: "flex",
//           backgroundColor: "#D6D6D6",
//           marginTop: "9px",
//           borderTop: "1px solid grey",
//           borderBottom: "1px solid grey",
//         }}
//       >
//         <div
//           style={{
//             backgroundColor: "#FFBD20",
//             display: "flex",
//             alignItems: "center",
//             cursor: "pointer",
//             paddingLeft: "8px",
//             width: "90px",
//           }}
//           onClick={() => setIsFilterVisible(!isFilterVisible)}
//         >
//           <img
//             src={Filter}
//             style={{ height: "24px", width: "24px" }}
//             alt="filter"
//           />
//           <span style={{ marginLeft: "10px", color: "#2F2E40" }}>Filter</span>
//         </div>

//         <div
//           className="col-tc"
//           style={{
//             marginLeft: "auto",
//             textAlign: "end",
//             marginRight: "8px",
//             display: "flex",
//             alignItems: "center",
//             position: "relative",
//             cursor: "pointer",
//             height: "40px",
//           }}
//           onClick={() => setIsColumnDivVisible(!isColumnDivVisible)}
//         >
//           <img
//             src={columnaddition}
//             style={{ height: "34px", width: "32px" }}
//             alt="column addition"
//           />
//           <span
//             style={{
//               marginLeft: "4px",
//               color: "#2F2E40",
//               marginRight: "14px",
//               fontSize: "14px",
//             }}
//           >
//             Column Selection
//           </span>
//           {isColumnDivVisible && (
//             <div ref={columnDropdownRef}>
//               <div className="arrow-up-tc"></div>
//               <div className="popupColumn-tc">
//                 {HEADERS.map((column, index) => (
//                   <div
//                     key={index}
//                     onClick={(e) => {
//                       e.stopPropagation();
//                       handleColumnSelection(column);
//                     }}
//                   >
//                     <input
//                       type="checkbox"
//                       id={`dpp-column-${index}`}
//                       value={column}
//                       className="square-checkbox-tc"
//                       checked={visibleColumns.includes(column)}
//                       disabled={LOCKED_COLUMNS.includes(column)}
//                       readOnly
//                     />
//                     <label htmlFor={`dpp-column-${index}`}>{column}</label>
//                   </div>
//                 ))}
//               </div>
//             </div>
//           )}
//         </div>
//       </div>

//       {/* table */}
//       {loading ? (
//         <div style={{ textAlign: "center", padding: "20px" }}>
//           Loading questions...
//         </div>
//       ) : error ? (
//         <div style={{ color: "red", padding: "10px" }}>{error}</div>
//       ) : sortedRows.length === 0 ? (
//         <div style={{ padding: "10px" }}>No data available</div>
//       ) : (
//         <>
//           <div className="table-wrapper-cols-selections">
//             <table className="table-tc" style={{ width: "100%" }}>
//               <thead>
//                 <tr>
//                   {orderedVisible.map((heading) => (
//                     <td
//                       key={heading}
//                       ref={heading === "Question Text" ? columnRef : null}
//                       className="Heading-tc resizable-header"
//                       style={{
//                         textAlign: "right",
//                         cursor: "pointer",
//                         position: "relative",
//                         width:
//                           heading === "Question Text"
//                             ? `${columnWidth}px`
//                             : "auto",
//                       }}
//                     >
//                       <span
//                         style={{
//                           display: "flex",
//                           alignItems: "left",
//                           justifyContent: "left",
//                         }}
//                       >
//                         {heading}
//                         {heading === "Question Text" && (
//                           <div
//                             className="resize-handle"
//                             onMouseDown={handleMouseDown}
//                           ></div>
//                         )}
//                         <img
//                           onClick={() => handleSort(heading)}
//                           src={updown}
//                           style={{
//                             height: "20px",
//                             width: "20px",
//                             marginLeft: "5px",
//                           }}
//                           alt="sort"
//                         />
//                       </span>
//                     </td>
//                   ))}
//                 </tr>
//               </thead>
//               <tbody>
//                 {paginatedRows.map((row, rowIndex) => (
//                   <tr
//                     key={row.id}
//                     className={rowIndex % 2 === 1 ? "even-row-tc" : ""}
//                     style={ Number(row.status) === 5 ? { backgroundColor: "#FDECEC" } : undefined }
//                   >
//                     {orderedVisible.map((column) => (
//                       <td
//                         key={column}
//                         style={{
//                           textAlign: "left",
//                           color: "#6A6A6A",
//                           verticalAlign: "middle",
//                           color: Number(row.status) === 5 ? "#D32F2F" : "#6A6A6A",
//                           whiteSpace: "nowrap",
//                           overflow: "hidden",
//                           textOverflow: "ellipsis",
//                           maxWidth:
//                             column === "Question Text"
//                               ? `${columnWidth}px`
//                               : undefined,
//                         }}
//                       >
//                         {column === "Question Text" ? (
//                           <div
//                             style={{
//                               color: "#337ab7",
//                               color: Number(row.status) === 5 ? "#D32F2F" : "#337ab7",
//                               cursor: "pointer",
//                               overflow: "hidden",
//                               textOverflow: "ellipsis",
//                               whiteSpace: "nowrap",
//                             }}
//                             onClick={() => setSelectedQuestionId(row.id)}
//                           >
//                             {renderTextSmall(row.cells[column])}
//                           </div>
//                         ) : (
//                           <span className="truncated-text-2">
//                             {row.cells[column]}
//                           </span>
//                         )}
//                       </td>
//                     ))}
//                   </tr>
//                 ))}
//               </tbody>
//             </table>
//           </div>

//           <div className="pagination-tc">
//             <button
//               onClick={() => handlePageChange(1)}
//               disabled={currentPage === 1}
//               className="extreme-left-tc-pagination"
//             >
//               <img src={leftArrow} alt="" />
//             </button>
//             <button
//               onClick={() => handlePageChange(currentPage - 1)}
//               disabled={currentPage === 1}
//               className="lefttc-pagination"
//             >
//               {"<"}
//             </button>
//             {renderPagination()}
//             <button
//               onClick={() => handlePageChange(currentPage + 1)}
//               disabled={currentPage === totalPages}
//               className="righttc-pagination"
//             >
//               {">"}
//             </button>
//             <button
//               onClick={() => handlePageChange(totalPages)}
//               disabled={currentPage === totalPages}
//               className="extreme-right-tc-pagination"
//             >
//               <img src={rightArrow} alt="" />
//             </button>
//           </div>
//         </>
//       )}

//       {/* filter sidebar */}
//       {isFilterVisible && (
//         <div className="filter-sidebar-tc">
//           <div className="filter-content-tc" style={{ position: "relative" }}>
//             <button
//               className="repman-cross-btn-HOD"
//               onClick={() => setIsFilterVisible(false)}
//             >
//               <p>X</p>
//             </button>
//             <h3 className="AdvanceF-heading-tc"> Filter</h3>
//             <div className="filter-apply-reset-ques-rev-dash">
//               <button
//                 className="create-test-section-btn"
//                 onClick={handleApplyFilter}
//               >
//                 {" "}
//                 APPLY{" "}
//               </button>
//               {isFiltersApplied && (
//                 <button
//                   className="create-test-section-btn"
//                   onClick={handleResetFilter}
//                 >
//                   {" "}
//                   RESET{" "}
//                 </button>
//               )}
//             </div>
//             <div className="filter-options-container">
//               {HEADERS.filter((h) => !NON_FILTER_COLUMNS.includes(h)).map(
//                 (header) => {
//                   const uniqueOptions = [
//                     ...new Set(
//                       rows
//                         .map((r) => r.cells[header])
//                         .filter(
//                           (v) =>
//                             v !== null &&
//                             v !== undefined &&
//                             String(v).trim() !== ""
//                         )
//                     ),
//                   ].sort((a, b) =>
//                     String(a).localeCompare(String(b), undefined, {
//                       numeric: true,
//                     })
//                   );
//                   return (
//                     <SelectedValuesDropdown
//                       key={header}
//                       label={header}
//                       width="300px"
//                       marginLeft="15px"
//                       marginTop="18px"
//                       setSelected={(selectedOption) =>
//                         setFilters((prev) => ({
//                           ...prev,
//                           [header]: selectedOption,
//                         }))
//                       }
//                       options={uniqueOptions.map((value) => ({
//                         label: String(value),
//                         id: value,
//                       }))}
//                       selected={filters[header] || ""}
//                       Height="auto"
//                       OverFlow="auto"
//                       minheight="200px"
//                     />
//                   );
//                 }
//               )}
//             </div>
//           </div>
//         </div>
//       )}

//       {/* faculty review modal */}
//       {selectedQuestionId !== null && (
//         <div
//           style={{
//             position: "fixed",
//             top: 0,
//             left: 0,
//             width: "100vw",
//             height: "100vh",
//             background: "rgba(0,0,0,0.5)",
//             zIndex: 1000,
//             display: "flex",
//             alignItems: "center",
//             justifyContent: "center",
//           }}
//         >
//           <div style={{ position: "relative", width: "84vw", height: "80vh", }} >
//             <div onClick={closeReviewModal} aria-label="Close" title="Close"
//               style={{
//                 position: "absolute",
//                 top: "-10px",
//                 right: "-38px",
//                 color: "#fff",
//                 fontSize: "30px",
//                 cursor: "pointer",
//                 userSelect: "none",
//                 zIndex: 1001,
//               }}
//             >
//               ×
//             </div>

//             <div
//               style={{
//                 background: "#fff",
//                 width: "100%",
//                 height: "100%",
//                 overflow: "auto",
//                 borderRadius: "14px",
//                 boxShadow: "0 4px 24px rgba(0,0,0,0.25)",
//               }}
//             >
//               <QuickQuesPaperModal
//                 isModal={true}
//                 onClose={closeReviewModal}
//                 modalSetId={parseQuesSetId}
//                 modalQuestionNumbers={allQuestionIds}
//                 modalInitialQuestionId={selectedQuestionId}
//                 setMessageBox={setMessageBox}
//                 setIsMessageBoxVisible={setIsMessageBoxVisible}
//                 setActiveImg={setActiveImg}
//                 dataClient={dataClient}
//               />
//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default QuickDPPSchedulingTable;